import { schemaFieldName } from '@/components/claims/newClaim/schema/schemaFieldName';
import { FormFieldSchemaType, type FormStepSchema } from '@/types';
import { isFuture } from 'date-fns';
import type { UseFormProps } from 'react-hook-form';
import {
  z,
  ZodArray,
  ZodISODate,
  ZodNumber,
  ZodObject,
  ZodOptional,
  ZodPipe,
  ZodString,
  type ZodCoercedNumber,
} from 'zod';

enum SchemaFieldErrorMessage {
  Required = 'Please provide a valid answer to this question',
  InvalidDate = 'Please select a valid date for this question',
  NotFuture = 'Date selected or entered cannot be a future date',
}

type ZodCoercedNumberString = ZodCoercedNumber<string>;

type ZodePipedNumberString = ZodPipe<ZodString, ZodCoercedNumberString>;

type ZodInputValue =
  | ZodString
  | ZodNumber
  | ZodISODate
  | ZodCoercedNumberString
  | ZodePipedNumberString
  | ZodArray<ZodString>
  | ZodOptional<ZodString>
  | ZodOptional<ZodNumber>
  | ZodOptional<ZodISODate>
  | ZodOptional<ZodCoercedNumberString>
  | ZodOptional<ZodePipedNumberString>
  | ZodOptional<ZodArray<ZodString>>;

type ZodOptionValue =
  | ZodString
  | ZodArray<ZodString>
  | ZodOptional<ZodString>
  | ZodOptional<ZodArray<ZodString>>;

type ZodSchemaFieldValue = ZodInputValue | ZodOptionValue;

type ZodFormSchemaObject = Record<string, ZodSchemaFieldValue>;

function generateInitialValuesForStep(stepSchema: FormStepSchema) {
  return stepSchema.form.fields.reduce<UseFormProps['defaultValues']>(
    (accumulator, { name, value }) =>
      value ? { ...(accumulator ?? {}), [name]: value } : accumulator,
    undefined,
  );
}

function generateZodFormSchemaForStep(stepSchema: FormStepSchema): ZodObject<ZodFormSchemaObject> {
  const errorObject = { error: SchemaFieldErrorMessage.Required };
  const schemaObject: ZodFormSchemaObject = {};

  stepSchema.form.fields.forEach(
    ({ name, type, options = [], isRequired = true, minLength, isVisibleWhen = [] }) => {
      // @TODO: Use 'type' to determine specific string format for field (e.g. date, time).
      let fieldValue: ZodSchemaFieldValue = z.string(errorObject).trim();

      switch (type) {
        case FormFieldSchemaType.Number: {
          fieldValue = z.coerce.number<string>(errorObject);
          break;
        }

        case FormFieldSchemaType.Checkbox: {
          if (options.length === 1 && typeof options[0] === 'number') {
            fieldValue = z.coerce.number<string>(errorObject);
            break;
          }

          if (options.length > 1) {
            fieldValue = z.array(z.string(), errorObject);
            break;
          }

          break;
        }

        case FormFieldSchemaType.Date: {
          fieldValue = z.iso.date({
            error: SchemaFieldErrorMessage.InvalidDate,
          });

          const { travelStartDate, travelEndDate } =
            schemaFieldName.tripDetails.annualMultiTripDetails;

          /**
           * @TODO: Should be a custom validation is the form 'schema'.
           * Selecting a future date is not allowed for any date field.
           * Only exceptions are 'travelStartDate' and 'travelEndDate'.
           */
          if (name !== travelStartDate && name !== travelEndDate) {
            fieldValue = fieldValue.refine(date => !isFuture(date), {
              error: SchemaFieldErrorMessage.NotFuture,
            });
          }
          break;
        }

        default:
      }

      if (isRequired) {
        fieldValue = fieldValue.min(1, errorObject);
      }

      if (minLength) {
        switch (type) {
          case FormFieldSchemaType.Number: {
            fieldValue = z
              .string()
              .min(minLength, errorObject)
              .pipe(z.coerce.number<string>(errorObject));
            break;
          }

          default: {
            fieldValue = fieldValue.min(minLength, errorObject);
          }
        }
      }

      /**
       * Hidden form fields are marked 'optional' here.
       * They will be marked as 'required' when visible.
       */
      if (!isRequired || isVisibleWhen.length) {
        fieldValue = fieldValue.optional();
      }

      schemaObject[name] = fieldValue;
    },
  );

  return z.object(schemaObject);
}

export { generateInitialValuesForStep, generateZodFormSchemaForStep, SchemaFieldErrorMessage };
export type { ZodFormSchemaObject, ZodInputValue, ZodOptionValue, ZodSchemaFieldValue };
